import { makeAutoObservable } from 'mobx'

import type {
  AssignedStudent,
  SelectionContext,
} from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import type { PendingSchedule } from '@/controller/worksheetAssignSchedule.controller'
import { worksheetAssignScheduleController } from '@/controller/worksheetAssignSchedule.controller'
import type { Student, Worksheet } from '@/dto'
import dateUtils from '@/utils/DateUtils'
import { toastError, toastSuccess } from '@/utils/toast'

import { SCHEDULE_STRINGS } from '../_constants/scheduleStrings'
import { confirmWarning, showNotice } from '../_utils/scheduleConfirm'
import { toastScheduleError } from '../_utils/scheduleError'
import { formatScheduledToast } from '../_utils/scheduleFormat'
import { ScheduleForm } from './ScheduleForm'

export type NewStudent = { id: Student['id']; name: Student['name'] }

/** 이 학습지에 걸린 대기 예약. 취소되면 none 으로 돌아가고 예약 학생은 신규 선택 학생으로 취급된다 */
export type Reservation =
  | { kind: 'none' }
  | { kind: 'existing'; batchId: string; studentIds: ReadonlySet<Student['id']> }

type Deps = {
  worksheetId: Worksheet['id']
  /** 이미 출제된 학생. 예약 학생을 뒤에 덧붙여 초기 오른쪽 목록을 만든다 */
  assignedLists: AssignedStudent[][]
  /** 토글 OFF 확정 시 즉시 출제. 실패를 안에서 처리했으면 false 를 돌려 모달을 유지시킨다 */
  onAssignNow: (students: NewStudent[]) => Promise<void | false>
  onScheduled?: (batchId: string) => void
  onChanged?: () => void
  now?: () => Date
}

const toReservation = (pending: PendingSchedule | null): Reservation =>
  pending
    ? { kind: 'existing', batchId: pending.batchId, studentIds: new Set(pending.studentIds) }
    : { kind: 'none' }

/**
 * 출제 모달 하나의 예약 상태. 모달을 열 때 만들고 닫으면 버린다.
 * 흐름은 항상 "확인(beforeConfirm·얼럿) → 실행(confirm·API) → 반영(상태·토스트)" 순서다
 */
export class AssignScheduleService {
  /** 대기 예약·대기 건수 조회. ready 가 되기 전에는 폼이 없다 */
  status: 'loading' | 'error' | 'ready' = 'loading'
  form: ScheduleForm | null = null
  reservation: Reservation = { kind: 'none' }
  initialAssignedLists: AssignedStudent[][] = []
  /** 즉시 출제 대상 중 이 학습지에 대기 예약이 걸린 학생. beforeConfirm 에서 묻고 confirm 에서 예약을 뺀다 */
  private pendingToCancel: PendingSchedule[] = []

  constructor(private readonly deps: Deps) {
    makeAutoObservable<AssignScheduleService, 'deps' | 'pendingToCancel'>(this, {
      deps: false,
      pendingToCancel: false,
    })
  }

  // ── 조회 ────────────────────────────────────────────────────────────────────
  load = async () => {
    this.setStatus('loading')
    try {
      const [pending, counts] = await Promise.all([
        worksheetAssignScheduleController.getPendingSchedule(this.deps.worksheetId),
        worksheetAssignScheduleController.getCounts(),
      ])
      this.setLoaded(pending, counts.waiting)
    } catch {
      this.setStatus('error')
    }
  }

  /** 목록 뷰에서 이 학습지의 예약을 취소하거나 바꿨을 수 있으니 돌아올 때 서버 상태에 다시 맞춘다 */
  syncWithServer = async (ctx: SelectionContext) => {
    const [pending] = await Promise.all([
      worksheetAssignScheduleController
        .getPendingSchedule(this.deps.worksheetId)
        .catch(() => undefined),
      this.refreshCounts(),
    ])
    if (pending !== undefined) this.applyServerReservation(pending, ctx)
  }

  /** 대기 건수는 안내용이라 실패해도 조용히 넘어간다 */
  refreshCounts = async () => {
    try {
      const counts = await worksheetAssignScheduleController.getCounts()
      this.form?.setWaitingCount(counts.waiting)
    } catch {
      /* 안내용 수치라 실패를 알리지 않는다 */
    }
  }

  isScheduling = (newStudentCount: number) =>
    !!this.form?.enabled && !this.form.getLimitError(newStudentCount)

  isPendingStudent = (id: Student['id']) =>
    this.reservation.kind === 'existing' && this.reservation.studentIds.has(id)

  // ── 예약 학생 제외 (오른쪽 → 왼쪽 이동 가로채기) ──────────────────────────
  removeStudents = async (students: Student[], ctx: SelectionContext) => {
    const { reservation } = this
    if (reservation.kind !== 'existing') return true
    const ids = students.map((s) => s.id).filter((id) => reservation.studentIds.has(id))
    if (ids.length === 0) return true
    if (!(await this.guardLocked())) return false
    if (!(await confirmWarning(SCHEDULE_STRINGS.removeStudentConfirm()))) return false

    try {
      await worksheetAssignScheduleController.cancelStudents(reservation.batchId, ids)
    } catch (error) {
      this.failAndRefresh(error)
      return false
    }
    this.dropStudentsFromReservation(ids, ctx)
    toastSuccess(SCHEDULE_STRINGS.removedStudent())
    this.deps.onChanged?.()
    return true
  }

  // ── 토글 OFF = 예약 취소. 예약 학생은 초기 상태에서 빠져 "바로 출제하기" 대상이 된다 ──
  toggle = async (next: boolean, ctx: SelectionContext) => {
    const { reservation, form } = this
    if (!form) return
    if (next || reservation.kind !== 'existing') {
      form.setEnabled(next)
      return
    }
    if (!(await this.guardLocked())) return
    if (!(await confirmWarning(SCHEDULE_STRINGS.toggleOffConfirm()))) return

    try {
      await worksheetAssignScheduleController.cancel(reservation.batchId)
    } catch (error) {
      this.failAndRefresh(error)
      return
    }
    this.dropReservation(ctx)
    this.deps.onChanged?.()
    void this.refreshCounts()
  }

  // ── 확정 1단계: 사용자에게 물을 것이 있으면 여기서 (로더 없음) ──────────────
  beforeConfirm = async (ctx: SelectionContext) => {
    if (this.reservation.kind === 'existing') return this.guardLocked()
    if (this.isScheduling(ctx.movedToRightItems.length)) return true
    return this.confirmAssignNowOverPending(ctx)
  }

  private confirmAssignNowOverPending = async (ctx: SelectionContext) => {
    this.pendingToCancel = await worksheetAssignScheduleController.getPendingTargets(
      [this.deps.worksheetId],
      ctx.movedToRightItems.map((s) => s.studentId),
    )
    if (this.pendingToCancel.length === 0) return true
    return confirmWarning(SCHEDULE_STRINGS.assignNowWarning())
  }

  // ── 확정 2단계: 서버 반영 (로더 아래). false 는 안내를 끝낸 실패 ──────────────
  confirm = async (ctx: SelectionContext) => {
    const newStudents = ctx.movedToRightItems.map((s) => ({ id: s.studentId, name: s.studentName }))
    if (this.reservation.kind === 'existing')
      return this.applyChanges(this.reservation, newStudents)
    // 새로 추가한 학생이 없으면 보낼 것이 없다. 빈 대상으로 예약이나 출제를 만들지 않고 그냥 닫는다
    if (newStudents.length === 0) return
    if (this.isScheduling(newStudents.length)) return this.createReservation(newStudents)
    return this.assignNow(newStudents)
  }

  private applyChanges = async (
    existing: Extract<Reservation, { kind: 'existing' }>,
    newStudents: NewStudent[],
  ) => {
    const form = this.form!
    try {
      // 시각 변경을 먼저. 학생 추가를 먼저 하고 실패하면 재시도에서 같은 학생이 다시 전송된다
      if (form.isTimeChanged) {
        const scheduledAt = form.validateForSubmit()
        if (!scheduledAt) {
          toastError(SCHEDULE_STRINGS.deadlinePassed)
          return false
        }
        await worksheetAssignScheduleController.reschedule(existing.batchId, scheduledAt)
        form.commitExistingAt(scheduledAt)
        // 뒤의 학생 추가가 실패해도 시각 변경은 서버에 남으므로 목록을 먼저 맞춘다
        this.deps.onChanged?.()
      }
      if (newStudents.length > 0) {
        await worksheetAssignScheduleController.addStudents(
          existing.batchId,
          newStudents.map((s) => s.id),
        )
      }
    } catch (error) {
      this.failAndRefresh(error)
      return false
    }
    toastSuccess(SCHEDULE_STRINGS.changed())
    this.deps.onChanged?.()
  }

  private createReservation = async (newStudents: NewStudent[]) => {
    const scheduledAt = this.form!.validateForSubmit()
    if (!scheduledAt) {
      toastError(SCHEDULE_STRINGS.deadlinePassed)
      return false
    }
    try {
      const { batchId } = await worksheetAssignScheduleController.create({
        worksheetId: this.deps.worksheetId,
        studentIdList: newStudents.map((s) => s.id),
        scheduledAt: dateUtils.formatLocalISO(scheduledAt),
      })
      toastSuccess(formatScheduledToast(scheduledAt))
      this.deps.onScheduled?.(batchId)
    } catch (error) {
      toastScheduleError(error)
      return false
    }
  }

  private assignNow = async (newStudents: NewStudent[]) => {
    // 예약에서 먼저 뺀다. 출제부터 하면 배치가 그 학생을 다시 출제하려다 실패 건으로 남는다
    if (!(await this.cancelPendingBeforeAssign())) return false
    return this.deps.onAssignNow(newStudents)
  }

  /** 뺀 학생은 목록에서 지우고 뱃지를 갱신해, 출제가 실패해 다시 눌러도 같은 학생을 또 빼지 않는다 */
  private cancelPendingBeforeAssign = async () => {
    for (const { batchId, studentIds } of this.pendingToCancel) {
      try {
        await worksheetAssignScheduleController.cancelStudents(batchId, studentIds)
      } catch (error) {
        toastScheduleError(error)
        return false
      }
      this.pendingToCancel = this.pendingToCancel.filter((p) => p.batchId !== batchId)
      this.deps.onChanged?.()
    }
    return true
  }

  // ── 상태 전이. 동기 메서드라 MobX action 으로 묶이며, await 뒤에서도 그대로 부른다 ──
  private setStatus = (status: AssignScheduleService['status']) => {
    this.status = status
  }

  private setLoaded = (pending: PendingSchedule | null, waitingCount: number) => {
    this.reservation = toReservation(pending)
    this.form = new ScheduleForm({
      initialScheduledAt: pending?.scheduledAt,
      waitingCount,
      now: this.deps.now,
    })
    this.initialAssignedLists = [
      ...this.deps.assignedLists,
      (pending?.studentIds ?? []).map((studentId) => ({ studentId })),
    ]
    this.status = 'ready'
  }

  /**
   * 서버의 대기 예약을 화면에 반영해 모달을 지금 새로 연 것과 같은 상태로 만든다.
   * 예약에서 빠진 학생은 선택 목록에서도 빼고, 다른 교사가 더한 학생은 "변경 없음" 인 예약 학생으로 받아들인다
   */
  private applyServerReservation = (pending: PendingSchedule | null, ctx: SelectionContext) => {
    const { reservation, form } = this
    if (reservation.kind !== 'existing' || !form) return
    const dropped = [...reservation.studentIds].filter((id) => !pending?.studentIds.includes(id))
    ctx.moveToLeft(dropped)

    if (!pending) {
      this.dropReservation(ctx)
      return
    }
    ctx.removeFromInitialState(dropped)
    const added = pending.studentIds.filter((id) => !reservation.studentIds.has(id))
    ctx.moveToRight(added)
    ctx.addToInitialState(added)
    this.reservation = toReservation(pending)
    if (form.existingAt?.getTime() !== pending.scheduledAt.getTime()) {
      form.commitExistingAt(pending.scheduledAt)
    }
  }

  /** 예약이 취소됐다. 예약 학생은 "변경 없음" 에서 빠져 신규 선택 학생으로 취급된다 */
  private dropReservation = (ctx: SelectionContext) => {
    if (this.reservation.kind !== 'existing') return
    this.dropStudentsFromReservation([...this.reservation.studentIds], ctx)
  }

  /** 예약에서 학생 일부가 빠졌다. 마지막 학생까지 빠지면 예약이 사라진 것과 같다 */
  private dropStudentsFromReservation = (ids: Student['id'][], ctx: SelectionContext) => {
    const { reservation } = this
    if (reservation.kind !== 'existing') return
    ctx.removeFromInitialState(ids)
    const remaining = new Set([...reservation.studentIds].filter((id) => !ids.includes(id)))
    if (remaining.size > 0) {
      this.reservation = { ...reservation, studentIds: remaining }
      return
    }
    this.reservation = { kind: 'none' }
    this.form?.clearExisting()
  }

  // ── 공통 ────────────────────────────────────────────────────────────────────
  /** 기존 예약이 5분 안이면 안내만 하고 막는다 */
  private guardLocked = async () => {
    if (!this.form?.isExistingLocked()) return true
    await showNotice(SCHEDULE_STRINGS.deadlineLocked())
    return false
  }

  private failAndRefresh = (error: unknown) => {
    toastScheduleError(error)
    void this.refreshCounts()
  }
}
